import { i18n } from "../internationalize/i18n.service"
import { AuthService } from "../auth/services/auth.service"
import type { PushService } from "./push.service"

import "./push.styles.scss"

const COPY = {
  en: { cta: "🔔 Enable notifications", done: "✓ Notifications enabled" },
  fr: { cta: "🔔 Activer les notifications", done: "✓ Notifications activées" },
}

/**
 * Compact opt-in CTA meant to be dropped inside an existing banner (Webflow embed:
 * `<tbw-push-cta></tbw-push-cta>`). Self-hiding: only shows for a logged-in user,
 * on a supported browser, who hasn't subscribed or been denied yet — so it never
 * nags people who already opted in (or can't). The browser prompt fires on click
 * (user gesture) → no auto-prompt penalty.
 */
export class PushCtaComponent extends HTMLElement {
  private authService: AuthService = window.tbwServices.authService
  private pushService: PushService = window.tbwServices.pushService
  private get t() { return COPY[i18n.getLanguage() === "fr" ? "fr" : "en"] }

  async connectedCallback() {
    if (!this.authService.isAuthenticated || !this.pushService.isSupported()) { this.hide(); return }
    const status = await this.pushService.getStatus()
    if (status !== "idle") { this.hide(); return } // already subscribed / denied / unsupported
    this.render()
  }

  private hide() { this.style.display = "none" }

  private render() {
    this.innerHTML = `<button class="tbw-push-cta__btn js-cta">${this.t.cta}</button>`
    const btn = this.querySelector<HTMLButtonElement>(".js-cta")!
    btn.addEventListener("click", async () => {
      btn.disabled = true
      try {
        const status = await this.pushService.enable()
        if (status === "subscribed") {
          btn.textContent = this.t.done
          setTimeout(() => this.hide(), 2500)
        } else {
          this.hide() // denied / unsupported → drop the CTA silently
        }
      } catch {
        this.hide()
      }
    })
  }
}
